"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo } from "react";
import { trackEvent } from "@/utils/gtm";
import EnrollButton from "@/components/LeadForm/EnrollButton";
import { FORM_CONTEXTS } from "@/utils/innovlistWidget";

const DEFAULT_COURSE_IMAGE = "/images/skill-1.jpg";
const DEFAULT_BLOG_IMAGE = "/images/skill-2.jpg";

export interface RelatedCourseItem {
  title?: string;
  slug?: string;
  image?: string;
  duration?: string;
  level?: string;
  courseDetails?: {
    duration?: string;
    level?: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface RelatedBlogItem {
  title?: string;
  slug?: string;
  image?: string;
  category?: string;
  excerpt?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface RelatedContentWidgetProps {
  courses?: RelatedCourseItem[];
  blogs?: RelatedBlogItem[];
  title?: string;
}

export default function RelatedContentWidget({
  courses = [],
  blogs = [],
  title = "Related Content",
}: RelatedContentWidgetProps) {
  const context = title === "Continue Learning" ? "blog" : "course";

  const normalizedCourses = useMemo(() => {
    return (Array.isArray(courses) ? courses : []).slice(0, 3).map((course) => ({
      title: String(course?.title || "Recommended Course"),
      slug: String(course?.slug || "/courses"),
      image: String(course?.image || DEFAULT_COURSE_IMAGE),
      duration: String(course?.duration || course?.courseDetails?.duration || "Flexible Duration"),
      level: String(course?.level || course?.courseDetails?.level || "All Levels"),
    }));
  }, [courses]);

  const normalizedBlogs = useMemo(() => {
    return (Array.isArray(blogs) ? blogs : []).slice(0, 3).map((post) => ({
      title: String(post?.title || "Recommended Blog"),
      slug: String(post?.slug || "/blog"),
      image: String(post?.image || DEFAULT_BLOG_IMAGE),
      category: String(post?.category || "IT Insights"),
      excerpt: String(post?.excerpt || "Practical insights to help you build your skills and accelerate your IT career growth."),
    }));
  }, [blogs]);

  if (normalizedCourses.length === 0 && normalizedBlogs.length === 0) {
    return null;
  }

  const onCardClick = (itemTitle: string) => {
    trackEvent("RelatedContent", "click", itemTitle, context);
  };

  return (
    <section className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20">
      <h2 className="text-[#1A1A1A] text-2xl md:text-3xl font-extrabold mb-8 text-left">{title}</h2>

      {context === "course" ? (
        <>
          {normalizedCourses.length > 0 ? (
            <div className="mb-10">
              <h3 className="text-[#1A1A1A] text-xl font-bold mb-5 text-left">Related Courses</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {normalizedCourses.map((course) => (
                  <article
                    key={course.slug}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all group flex flex-col"
                  >
                    <div className="relative h-[200px]">
                      <Image
                        src={course.image}
                        alt={course.title}
                        fill
                        loading="lazy"
                        className="object-cover"
                      />
                    </div>
                    <div className="p-6 text-left flex flex-col flex-grow">
                      <h4 className="text-[#1A1A1A] text-lg font-bold mb-3 group-hover:text-[#1B61FF] transition-colors leading-tight line-clamp-2">
                        {course.title}
                      </h4>
                      <div className="text-[16px] text-[#666666] space-y-1 mb-5">
                        <p>Duration: {course.duration}</p>
                        <p>Level: {course.level}</p>
                      </div>
                      <Link
                        prefetch={true}
                        href={course.slug}
                        onClick={() => onCardClick(course.title)}
                        className="mt-auto inline-flex items-center gap-2 text-[#1B61FF] text-sm font-bold"
                      >
                        View Course
                        <span aria-hidden="true">→</span>
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ) : null}

          {normalizedBlogs.length > 0 ? (
            <div>
              <h3 className="text-[#1A1A1A] text-xl font-bold mb-5 text-left">From the Blog</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {normalizedBlogs.slice(0, 2).map((post) => (
                  <article
                    key={post.slug}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all group flex flex-col p-6"
                  >
                    <p className="text-xs uppercase tracking-wider font-semibold text-[#1B61FF] mb-3">{post.category}</p>
                    <h4 className="text-[#1A1A1A] text-lg font-bold mb-3 group-hover:text-[#1B61FF] transition-colors leading-tight line-clamp-2">
                      {post.title}
                    </h4>
                    <p className="text-[#666666] text-sm leading-relaxed mb-5 line-clamp-3">{post.excerpt}</p>
                    <Link
                      prefetch={true}
                      href={post.slug}
                      onClick={() => onCardClick(post.title)}
                      className="mt-auto inline-flex items-center gap-2 text-[#1B61FF] text-sm font-bold"
                    >
                      Read Blog
                      <span aria-hidden="true">→</span>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <>
          {normalizedBlogs.length > 0 ? (
            <div className="mb-10">
              <h3 className="text-[#1A1A1A] text-xl font-bold mb-5 text-left">Related Blog Posts</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {normalizedBlogs.map((post) => (
                  <article
                    key={post.slug}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all group flex flex-col"
                  >
                    <div className="relative h-[200px]">
                      <Image
                        src={post.image}
                        alt={post.title}
                        fill
                        loading="lazy"
                        className="object-cover"
                      />
                    </div>
                    <div className="p-6 text-left flex flex-col flex-grow">
                      <p className="text-xs uppercase tracking-wider font-semibold text-[#1B61FF] mb-3">{post.category}</p>
                      <h4 className="text-[#1A1A1A] text-lg font-bold mb-3 group-hover:text-[#1B61FF] transition-colors leading-tight line-clamp-2">
                        {post.title}
                      </h4>
                      <p className="text-[#666666] text-[18px] leading-relaxed mb-5 line-clamp-3">{post.excerpt}</p>
                      <Link
                        prefetch={true}
                        href={post.slug}
                        onClick={() => onCardClick(post.title)}
                        className="mt-auto inline-flex items-center gap-2 text-[#1B61FF] text-sm font-bold"
                      >
                        Read More
                        <span aria-hidden="true">→</span>
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ) : null}

          {normalizedCourses.length > 0 ? (
            <div>
              <h3 className="text-[#1A1A1A] text-xl font-bold mb-5 text-left">Recommended Courses</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {normalizedCourses.slice(0, 2).map((course) => (
                  <article
                    key={course.slug}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all group flex flex-col p-6"
                  >
                    <h4 className="text-[#1A1A1A] text-lg font-bold mb-3 group-hover:text-[#1B61FF] transition-colors leading-tight line-clamp-2">
                      {course.title}
                    </h4>
                    <div className="text-sm text-[#666666] space-y-1 mb-5">
                      <p>Duration: {course.duration}</p>
                      <p>Level: {course.level}</p>
                    </div>
                    <div className="mt-auto">
                      <EnrollButton
                        label="Enroll"
                        variant="outline"
                        size="sm"
                        context={FORM_CONTEXTS.ENROLL_NOW}
                        icon={false}
                      />
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

